import React, { useCallback, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { Checkbox, FormControlLabel } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { styled, alpha } from '@mui/material/styles';
import axios from 'src/axios';
import PasswordInput from 'src/components/UI/input/PasswordInput';
import TextInput from '../UI/input/TextInput';
import { ThemeContext, UserContext } from 'src/store/ContextStore';
import {
  APICONSTANTS,
  CONSTANTS,
  RECAPTCHA_SITE_KEY
} from 'src/components/Constants';
import {
  injectGoogleCaptchaScript,
  validateEmail,
  getPractitionerDisplay
} from 'src/util/util';
import { originWithBase } from 'src/util/basePath';
import AuthLayout from '../UI/auth/AuthLayout';
import MfaVerify from './MfaVerify';
import {
  Fade,
  Divider,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';

const SMART_PENDING_STORAGE_KEY = 'smart_emr_pending_patient';

const Form = styled('form')(({ theme }) => ({
  flexDirection: 'column',
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(3),
  width: '100%'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingBlock: 10,
  // Give the enabled primary action an intentional, themed elevation so it
  // never reads as accidentally greyed. The disabled state keeps MUI's default
  // (which is correct — the button IS disabled until the form is valid).
  '&:not(.Mui-disabled)': {
    boxShadow: `0 8px 20px -8px ${alpha(theme.palette.primary.main, 0.5)}`
  }
}));

const StyledLink = styled(Link)(({ theme }) => ({
  fontWeight: 'bold',
  textDecoration: 'none',
  color: theme.palette.primary.main
}));

const Adminlogin = () => {
  const { setTheme } = React.useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const nextPath = searchParams.get('next') || null;
  const initialRedirectUrl = searchParams.get('redirect') || null;

  const [redirectUrl, setRedirectUrl] = useState(() => {
    return (
      initialRedirectUrl || localStorage.getItem('loginRedirectUrl') || null
    );
  });

  React.useEffect(() => {
    if (initialRedirectUrl) {
      setRedirectUrl(initialRedirectUrl);
    }
  }, [initialRedirectUrl]);

  React.useEffect(() => {
    if (redirectUrl) {
      localStorage.setItem('loginRedirectUrl', redirectUrl);
    }
  }, [redirectUrl]);

  const isResetingPassword =
    window.location.pathname === CONSTANTS.resetPasswordPath;
  const { setUser, user } = React.useContext(UserContext);
  const [smartPending, setSmartPending] = React.useState(null);
  const [practitionerDisplay, setPractitionerDisplay] = React.useState(null);
  const [code, setCode] = React.useState('');
  const [failedAttemp, setFailedAttemp] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [alert, setAlert] = React.useState({ status: false });
  const [is2FA, setIs2FA] = useState(false);
  const [disableBackButton, setDisableBackButton] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [rememberMe, setRememberMe] = useState(false);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [groupSwitchLoading, setGroupSwitchLoading] = useState(false);
  const [rememberGroup, setRememberGroup] = useState(false);

  const recaptcha = useRef('');

  React.useEffect(() => {
    axios
      .get(APICONSTANTS.recaptcha)
      .then((res) => {
        injectGoogleCaptchaScript(null, res.data.siteKey);
        recaptcha.current = res.data.siteKey;
      })
      .catch((err) => console.error(err));

    axios
      .get(APICONSTANTS.defaultTheme)
      .then((res) => {
        if (res.data?.theme) setTheme(res.data.theme);
      })
      .catch((/*err*/) => {
        setTheme(CONSTANTS.customTheme);
      });
  }, [setTheme]);

  React.useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(SMART_PENDING_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setSmartPending(parsed);

        const pract = parsed?.practitioner || null;

        const display = getPractitionerDisplay(pract);
        setPractitionerDisplay(display);
      }
    } catch (e) {
      console.warn(
        '[SMART FHIR] Failed to read pending EMR patient in Adminlogin',
        e
      );
    }
  }, []);

  React.useEffect(() => {
    if (is2FA) {
      // dummy_state, temporary implementation
      window.history.pushState(null, '');

      const handlePopState = () => {
        window.history.pushState(null, '');
      };

      window.addEventListener('popstate', handlePopState);

      setDisableBackButton(true);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    } else {
      if (disableBackButton) {
        window.history.back();
        setDisableBackButton(false);
      }
    }
  }, [is2FA, disableBackButton]);

  React.useEffect(() => {
    if (!is2FA && !isResetingPassword) {
      const savedUsername = localStorage.getItem('rememberedUsername');
      if (savedUsername) {
        setEmail(savedUsername);
        setRememberMe(true);
      }
    }
  }, [is2FA, isResetingPassword]);

  const isValid = () => {
    return isResetingPassword ? !!email : !!email && !!password;
  };

  const onTempAccess = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!recaptcha.current) {
      axios
        .post(APICONSTANTS.login, { email, password, grToken: 'token' })
        .then((res) => {
          setLoading(false);
          if (res.data?.status === CONSTANTS.client_status.ACTIVE) {
            setUser(res.data);
            setIs2FA(true);

            if (rememberMe) {
              localStorage.setItem('rememberedUsername', email);
            } else {
              localStorage.removeItem('rememberedUsername');
            }
          } else {
            setAlert({
              status: true,
              severity: 'error',
              msg: 'This account has been deactivated!'
            });
            setPassword('');
          }
        })
        .catch(() => {
          setAlert({
            status: true,
            severity: 'error',
            msg: 'Invalid email or password!'
          });
          setLoading(false);
        });
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (alert.status) setAlert((prev) => ({ ...prev, status: false }));
    if (!validateEmail(email)) {
      setAlert({ status: true, severity: 'error', msg: 'Invalid email!' });
      return;
    }
    setLoading(true);

    window.grecaptcha?.ready(function () {
      try {
        window.grecaptcha
          .execute(recaptcha.current, {
            action: isResetingPassword ? 'passwordReset' : 'login'
          })
          .then(function (grToken) {
            if (isResetingPassword) {
              axios
                .post(APICONSTANTS.resetPasswordEmail, { email, grToken })
                .then(() => {
                  setAlert({
                    status: true,
                    severity: 'success',
                    msg: 'Password Reset Email sent!'
                  });
                  setTimeout(() => {
                    setAlert((prev) => ({ ...prev, status: false }));
                    navigate(CONSTANTS.signinPath);
                  }, 3000);
                  setLoading(false);
                })
                .catch(() => {
                  setAlert({
                    status: true,
                    severity: 'error',
                    msg: 'Invalid email or password!'
                  });
                  setLoading(false);
                });
            } else if (password) {
              axios
                .post(APICONSTANTS.login, { email, password, grToken })
                .then((res) => {
                  setLoading(false);
                  if (res.data?.status === CONSTANTS.client_status.ACTIVE) {
                    setUser(res.data);
                    setIs2FA(true);

                    if (rememberMe) {
                      localStorage.setItem('rememberedUsername', email);
                    } else {
                      localStorage.removeItem('rememberedUsername');
                    }
                  } else {
                    setAlert({
                      status: true,
                      severity: 'error',
                      msg: 'This account has been deactivated!'
                    });
                    setPassword('');
                  }
                })
                .catch((error) => {
                  setPassword('');
                  setEmail('');
                  setLoading(false);
                  if (error.response.status === 401) {
                    setAlert({
                      status: true,
                      severity: 'error',
                      msg: 'Invalid email/password'
                    });
                  }
                  if (error?.message.includes('429')) {
                    setAlert({
                      status: true,
                      severity: 'error',
                      msg: 'Too many failed attempts. Please try again in 5 minutes.'
                    });
                  } else {
                    setFailedAttemp(true);
                  }
                });
            }
          });
      } catch (error) {
        setAlert({
          status: true,
          severity: 'error',
          msg: 'Bot Detected'
        });
        setLoading(false);
      }
    });
  };

  const navigateToDashboard = useCallback(async () => {
    await axios
      .get(APICONSTANTS.getCustomTheme)
      .then((res) => {
        if (res.data) setTheme(res.data);
      })
      .catch((/*err*/) => {
        setTheme(CONSTANTS.customTheme);
      });

    try {
      const pendingSmartPatient = window.sessionStorage.getItem(
        SMART_PENDING_STORAGE_KEY
      );
      if (pendingSmartPatient) {
        window.location.href = `${originWithBase()}/smart/callback`;
        return;
      }
    } catch (e) {
      console.warn(
        '[SMART FHIR] Failed to read pending EMR patient in Adminlogin',
        e
      );
    }

    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else if (nextPath) {
      navigate(nextPath);
    } else {
      navigate(CONSTANTS.adminPath);
    }
  }, [navigate, nextPath, redirectUrl, setTheme]);

  const handleGroupSelect = useCallback(
    async (userGroupId) => {
      setGroupSwitchLoading(true);
      try {
        await axios.post(APICONSTANTS.switchUserGroup, {
          userGroupId,
          setAsDefault: rememberGroup
        });
        setShowGroupSelector(false);
        await navigateToDashboard();
      } catch (err) {
        console.error('Failed to switch usergroup', err);
      } finally {
        setGroupSwitchLoading(false);
      }
    },
    [navigateToDashboard, rememberGroup]
  );

  const verify2FACallback = useCallback(
    async (e) => {
      e.preventDefault();
      if (verifying) return;
      setVerifying(true);
      try {
        const response = await axios.post(`${APICONSTANTS.login}/add-2fa`, {
          code,
          secret: user?.twoFactorAuthSecret,
          twoFactorAuthEnabled: user?.twoFactorAuthEnabled,
          email: user?.email
        });
        if (response?.data?.isValid) {
          localStorage.setItem('2FAloggedin', 'true');

          // If the user belongs to multiple groups, prompt them to choose one
          if (user?.needsGroupSelection) {
            setShowGroupSelector(true);
            return;
          }

          await navigateToDashboard();
        } else {
          setCodeError({ msg: 'Wrong authenticator code! Try again.' });
          setCode(''); // clear the boxes so the user can re-enter cleanly
        }
      } catch (error) {
        console.error(error);
        setCodeError({ msg: 'Something went wrong verifying your code.' });
      } finally {
        setVerifying(false);
      }
    },
    [
      code,
      verifying,
      navigate,
      nextPath,
      redirectUrl,
      setTheme,
      user?.email,
      user?.twoFactorAuthEnabled,
      user?.twoFactorAuthSecret
    ]
  );
  const [backupLoading, setBackupLoading] = React.useState(false);

  // Function to handle the backup code request
  const handleBackupCode = async (e) => {
    e.preventDefault();
    setBackupLoading(true);
    try {
      await axios.post(`${APICONSTANTS.login}/generate-onetime-code`, {
        email
      });
      setCodeError({
        msg: 'A one time passcode has been sent to the registered email address.',
        type: 'info'
      });
      setBackupLoading(false);
    } catch (err) {
      setCodeError({ msg: 'Failed to generate one time passcode!' });
      setBackupLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    window.location.href = `${originWithBase()}${APICONSTANTS.ssoLogin}`;
  };

  return (
    <AuthLayout
      topRight={
        practitionerDisplay ? (
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {`Launched by ${practitionerDisplay}${
              smartPending?.patientData?.fhirSource
                ? ` from ${smartPending.patientData.fhirSource}`
                : ''
            }`}
          </Typography>
        ) : null
      }
    >
      {!is2FA ? (
        <Form
          className="user-form"
          onSubmit={recaptcha.current ? onSubmit : onTempAccess}
        >
          {alert.status && (
            <Grid size={12}>
              <Fade in={alert.status} unmountOnExit>
                <Alert severity={alert.severity}>{alert.msg}</Alert>
              </Fade>
            </Grid>
          )}
          <Grid size={12}>
            <TextInput
              label="Email"
              type="email"
              name="email"
              variant="outlined"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={
                !!(alert.status && !validateEmail(email) && !isResetingPassword)
              }
              autoFocus
            />
          </Grid>
          {!isResetingPassword && (
            <>
              <Grid size={12}>
                <PasswordInput
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={alert.status}
                />
              </Grid>

              <FormControlLabel
                control={
                  <Checkbox
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label={'Remember Me'}
              />
            </>
          )}
          <Grid size={12}>
            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading || !isValid()}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isResetingPassword ? (
                'Send'
              ) : (
                'Login'
              )}
            </StyledButton>
          </Grid>
          {!isResetingPassword && (
            <Grid
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
              size={12}
            >
              <StyledLink
                to={CONSTANTS.resetPasswordPath}
                onClick={() => {
                  setEmail('');
                  setPassword('');
                  setAlert({
                    status: true,
                    severity: 'info',
                    msg: 'Please enter your email to reset your password.'
                  });
                }}
              >
                Forgot password?
              </StyledLink>
            </Grid>
          )}
          {!isResetingPassword && !is2FA && (
            <>
              <Grid size={12}>
                <Divider sx={{ my: 2, textAlign: 'center' }}>
                  <Typography variant="body1" color="textSecondary">
                    or
                  </Typography>
                </Divider>
              </Grid>
              <Grid size={12}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleMicrosoftLogin}
                  fullWidth
                >
                  Login with SSO
                </Button>
              </Grid>
            </>
          )}
        </Form>
      ) : user?.qrCodeImgUrl || user?.twoFactorAuthEnabled ? (
        <MfaVerify
          code={code}
          setCode={setCode}
          onVerify={verify2FACallback}
          verifying={verifying}
          onCancel={() => window.location.reload()}
          codeError={codeError}
          qrCodeImgUrl={user?.qrCodeImgUrl}
          showBackup={!user?.qrCodeImgUrl}
          onBackupCode={handleBackupCode}
          backupLoading={backupLoading}
          backupDisabled={backupLoading || !email || !validateEmail(email)}
        />
      ) : null}
      {/* Group selector dialog — shown after successful 2FA for multi-group users */}
      <Dialog
        open={showGroupSelector}
        disableEscapeKeyDown
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <GroupsIcon />
            Select a Usergroup
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important', pb: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Your account belongs to multiple usergroups. Please select which one
            you want to access.
          </Alert>
          <List disablePadding>
            {user?.groups?.map((g) => (
              <ListItem key={g.userGroup?._id} disablePadding>
                <ListItemButton
                  onClick={() => handleGroupSelect(g.userGroup?._id)}
                  disabled={groupSwitchLoading}
                >
                  <ListItemIcon>
                    {groupSwitchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <GroupsIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={g.userGroup?.label}
                    secondary={
                      CONSTANTS.userTypeOptionsMap?.[g.userType] ?? g.userType
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Box
            sx={{
              mt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              pt: 2
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberGroup}
                  onChange={(e) => setRememberGroup(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary'
                  }}
                >
                  Remember my choice — don&apos;t ask me again at login
                </Typography>
              }
            />
          </Box>
        </DialogContent>
      </Dialog>
    </AuthLayout>
  );
};

export default Adminlogin;
